"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { playSound } from "@/lib/game/audio";
import { randomPlanetCount } from "@/lib/game/difficulty";
import { formatStarReward } from "@/lib/game/stars";
import { rocketPlacementsForDisplay } from "@/lib/game/rocket";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import { DragChip, DropSlot } from "./DragChip";
import { DragDropProvider } from "./DragDropContext";
import { GameIconButton } from "../game-ui";
import type { PlanetJumpSceneHandle } from "./PixiPlanetJump";
import { useGame } from "../GameProvider";

const PixiPlanetJump = dynamic(
  () => import("./PixiPlanetJump").then((m) => m.PixiPlanetJump),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center bg-indigo-950 text-sm text-violet-200">
        Loading planet path…
      </div>
    ),
  },
);

const NUMBERS = ["1", "2", "3", "4", "5", "6"];

function PlanetJumpGame({
  challengeKey,
  onPlayAgain,
}: {
  challengeKey: number;
  onPlayAgain: () => void;
}) {
  const { completeActivity, progress } = useGame();
  const sceneRef = useRef<PlanetJumpSceneHandle | null>(null);
  const activityId = "planet-jump";
  const difficulty = progress.difficulty;
  const rocketPlacements = useMemo(
    () => rocketPlacementsForDisplay(progress.builtRocket),
    [progress.builtRocket],
  );

  const planetCount = useMemo(
    () => randomPlanetCount(difficulty),
    [difficulty, challengeKey],
  );
  const jumpCount = planetCount + 1;

  const [answer, setAnswer] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "flying" | "right">("idle");
  const checkingRef = useRef(false);

  const locked = feedback === "flying" || feedback === "right";

  const runCheck = useCallback(
    async (value: string) => {
      if (checkingRef.current || locked) return;
      checkingRef.current = true;

      if (Number(value) === jumpCount) {
        setFeedback("flying");
        playSound("correct");
        await sceneRef.current?.playVictoryFlight();
        completeActivity(activityId, difficulty);
        setFeedback("right");
      } else {
        setFeedback("wrong");
        playSound("wrong");
        sceneRef.current?.resetRocket();
      }

      checkingRef.current = false;
    },
    [jumpCount, locked, activityId, difficulty, completeActivity],
  );

  useEffect(() => {
    setAnswer(null);
    setFeedback("idle");
    checkingRef.current = false;
  }, [difficulty, challengeKey]);

  useEffect(() => {
    if (!answer || locked) return;
    void runCheck(answer);
  }, [answer, locked, runCheck]);

  const handleDrop = useCallback(
    (value: string, slotId: string | null) => {
      if (locked) return;
      if (!slotId || slotId !== "answer") return;
      setAnswer(value);
      setFeedback("idle");
      checkingRef.current = false;
    },
    [locked],
  );

  const handlePickFromSlot = useCallback(() => {
    if (locked) return;
    setAnswer(null);
    setFeedback("idle");
    sceneRef.current?.resetRocket();
  }, [locked]);

  const handleTryAgain = () => {
    setAnswer(null);
    setFeedback("idle");
    checkingRef.current = false;
    sceneRef.current?.resetRocket();
  };

  return (
    <DragDropProvider onDrop={handleDrop}>
      <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-gradient-to-b from-indigo-950 to-slate-900 text-white">
        <p className="shrink-0 py-2 text-center text-2xl font-black text-violet-200">
          🚀 → ? → 🌀
        </p>

        <div className="min-h-0 flex-1 overflow-hidden">
          <PixiPlanetJump
            planetCount={planetCount}
            placements={rocketPlacements}
            onSceneReady={(handle) => {
              sceneRef.current = handle;
            }}
          />
        </div>

        <div className="shrink-0 space-y-2 border-t border-violet-500/30 bg-slate-900/80 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="flex flex-col items-center gap-1.5">
            <DropSlot
              id="answer"
              label="Drag number here"
              value={answer}
              dragValue={answer}
              editable={!locked}
              onPickFromSlot={handlePickFromSlot}
              className="h-16 w-24 text-lg"
            />
            {feedback === "wrong" && (
              <p className="text-xs font-semibold text-rose-300">✗</p>
            )}
            {feedback === "flying" && (
              <p className="text-xs font-semibold text-cyan-200">Flying to the stargate…</p>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-1.5">
            {NUMBERS.map((n) => (
              <DragChip
                key={n}
                value={n}
                disabled={locked || answer === n}
                className="h-11 w-11 text-base sm:h-12 sm:w-12 sm:text-lg"
              />
            ))}
          </div>

          {feedback === "wrong" && (
            <div className="flex justify-center">
              <GameIconButton
                icon="retry"
                tone="sky"
                size="md"
                label="Try again"
                onClick={handleTryAgain}
              />
            </div>
          )}
        </div>

        <ActivitySuccessOverlay
          show={feedback === "right"}
          title="Stargate reached!"
          theme="indigo"
          rewardLabel={formatStarReward(difficulty)}
          onPlayAgain={onPlayAgain}
        />
      </div>
    </DragDropProvider>
  );
}

export function PlanetJumpActivity() {
  const { progress, setDifficulty, trackActivitySession } = useGame();
  const [challengeKey, setChallengeKey] = useState(0);

  useEffect(() => {
    trackActivitySession("planet-jump");
  }, [trackActivitySession]);

  const handleNewChallenge = () => {
    setChallengeKey((k) => k + 1);
  };

  return (
    <ActivityLayout
      title="Planet Jumps"
      showRocketBuddy={false}
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={(level) => {
              setDifficulty(level);
              handleNewChallenge();
            }}
            compact
          />
        </div>
      }
    >
      <PlanetJumpGame
        key={`${progress.difficulty}-${challengeKey}`}
        challengeKey={challengeKey}
        onPlayAgain={handleNewChallenge}
      />
    </ActivityLayout>
  );
}
